import React, { useState, useEffect } from 'react';




import {
  getAlertPreferences,
  batchUpdateAlertPreferences,
  resetAlertPreferences
} from '../../../services/alertPreferencesService';
import Icon from '../../../components/AppIcon';


const AlertPreferencesSettings = () => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const alertTypeLabels = {
    ai_match: {
      title: 'AI Match Recommendations',
      description: 'Get notified when AI identifies player-club matches'
    },
    deal_progress: {
      title: 'Deal Progress Updates',
      description: 'Track status changes in transfer deals and negotiations'
    },
    market_value: {
      title: 'Market Value Changes',
      description: 'Monitor player market value fluctuations and trends'
    },
    scout_response: {
      title: 'Scout Responses',
      description: 'Receive replies from scouts and talent evaluators'
    }
  };

  const frequencyOptions = [
    { value: 'instant', label: 'Instant' },
    { value: 'hourly', label: 'Hourly Digest' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Digest' },
    { value: 'never', label: 'Never' }
  ];

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data, error } = await getAlertPreferences();
      if (error) {
        setMessage({ type: 'error', text: 'Failed to load preferences' });
      } else {
        setPreferences(data || []);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      setMessage({ type: 'error', text: 'Failed to load preferences' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = (alertType) => {
    setPreferences(prev =>
      prev?.map(pref =>
        pref?.alertType === alertType
          ? { ...pref, enabled: !pref?.enabled }
          : pref
      )
    );
  };

  const handleFrequencyChange = (alertType, frequency) => {
    setPreferences(prev =>
      prev?.map(pref =>
        pref?.alertType === alertType
          ? { ...pref, frequency }
          : pref
      )
    );
  };

  const handleDeliveryMethodToggle = (alertType, method) => {
    setPreferences(prev =>
      prev?.map(pref => {
        if (pref?.alertType === alertType) {
          if (method === 'email') {
            return { ...pref, emailEnabled: !pref?.emailEnabled };
          } else if (method === 'sms') {
            return { ...pref, smsEnabled: !pref?.smsEnabled };
          } else if (method === 'push') {
            return { ...pref, pushEnabled: !pref?.pushEnabled };
          }
        }
        return pref;
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { error } = await batchUpdateAlertPreferences(preferences);
      if (error) {
        setMessage({ type: 'error', text: 'Failed to save preferences' });
      } else {
        setMessage({ type: 'success', text: 'Alert preferences saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data, error } = await resetAlertPreferences();
      if (error) {
        setMessage({ type: 'error', text: 'Failed to reset preferences' });
      } else {
        setPreferences(data || []);
        setMessage({ type: 'success', text: 'Preferences reset to defaults' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error resetting preferences:', error);
      setMessage({ type: 'error', text: 'Failed to reset preferences' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground font-headline flex items-center gap-2">
            <Icon name="AlertCircle" size={24} className="text-primary" />
            Alert Preferences
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Customize your alert preferences</p>
        </div>
      </div>
      <div className="space-y-4">
        {preferences?.map((pref) => (
          <div
            key={pref?.id}
            className="p-4 rounded-lg border border-border hover:bg-muted transition-colors duration-300"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-start gap-3 flex-1">
                <div className={`w-10 h-10 rounded-lg ${pref?.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={pref?.icon} size={20} />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground mb-1">{pref?.label}</p>
                  <p className="text-sm text-muted-foreground">{pref?.description}</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={pref?.enabled}
                  onChange={() => {}}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            {pref?.options && (
              <div className="ml-13 space-y-2">
                {pref?.options?.map((option) => (
                  <label key={option?.id} className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                    <input
                      type="checkbox"
                      checked={option?.enabled}
                      onChange={() => {}}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                    />
                    {option?.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AlertPreferencesSettings;